import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import type * as React from 'react';

import { cn } from '../../styles/lib/utils';

export const BADGE_KINDS = ['status', 'outline', 'count'] as const;
export const BADGE_SIZES = ['standard', 'small'] as const;
export const STATUS_COLORS = ['red', 'green', 'grey', 'orange', 'blue', 'yellow', 'purple', 'pink'] as const;
export const OUTLINE_COLORS = ['red', 'green', 'blue', 'grey', 'orange', 'yellow'] as const;

export type BadgeKind = (typeof BADGE_KINDS)[number];
export type BadgeSize = (typeof BADGE_SIZES)[number];
export type BadgeColor = (typeof STATUS_COLORS)[number];

export const badgeVariants = cva(
  'inline-flex items-center gap-2.5 rounded-[8px] px-2 py-0.5 font-noto font-medium max-w-full text-grey-90',
  {
    variants: {
      kind: {
        status: '',
        outline: 'border',
        count: 'min-h-5 justify-center border border-grey-30 bg-grey-10 text-primary-dark font-noto-mono',
      },
      color: {
        red: '',
        green: '',
        grey: '',
        orange: '',
        blue: '',
        yellow: '',
        purple: '',
        pink: '',
      },
      size: {
        standard: 'text-xs',
        small: 'text-2xs leading-tight',
      },
    },
    compoundVariants: [
      { kind: 'status', color: 'red', class: 'bg-red-20' },
      { kind: 'status', color: 'green', class: 'bg-green-20' },
      { kind: 'status', color: 'grey', class: 'bg-grey-30' },
      { kind: 'status', color: 'orange', class: 'bg-orange-20' },
      { kind: 'status', color: 'blue', class: 'bg-blue-20' },
      { kind: 'status', color: 'yellow', class: 'bg-yellow-20' },
      { kind: 'status', color: 'purple', class: 'bg-purple-20' },
      { kind: 'status', color: 'pink', class: 'bg-pink-20' },
      { kind: 'outline', color: 'red', class: 'bg-red-10 border-red-20' },
      { kind: 'outline', color: 'green', class: 'bg-green-10 border-green-20' },
      { kind: 'outline', color: 'blue', class: 'bg-blue-10 border-blue-20' },
      { kind: 'outline', color: 'grey', class: 'bg-grey-10 border-grey-20' },
      { kind: 'outline', color: 'orange', class: 'bg-orange-10 border-orange-20' },
      { kind: 'outline', color: 'yellow', class: 'bg-yellow-10 border-yellow-20' },
    ],
    defaultVariants: {
      kind: 'status',
      color: 'grey',
      size: 'standard',
    },
  },
);

// `color` names the badge hue and shadows span's deprecated HTML color attribute.
type BadgeBaseProps = Omit<React.ComponentProps<'span'>, 'color'> & {
  size?: BadgeSize;
  asChild?: boolean;
};

export type BadgeProps = BadgeBaseProps &
  (
    | { kind?: 'status'; color?: (typeof STATUS_COLORS)[number] }
    | { kind: 'outline'; color: (typeof OUTLINE_COLORS)[number] }
    | { kind: 'count'; color?: never }
  );

/**
 * Label pill with three kinds: `status` (soft fill, default) and `outline` (bordered) for
 * state labels, and `count` (monospace numeral, ignores color) for numeric badges.
 */
function Badge({ className, kind, color, size, asChild = false, ...props }: BadgeProps) {
  const Comp = asChild ? Slot : 'span';

  return <Comp data-slot="badge" className={cn(badgeVariants({ kind, color, size, className }))} {...props} />;
}

export { Badge };
